import { Chip, cn, Tooltip } from '@c2fo/liquidity';
import { RecurringRule } from '@/enterprise/data/useRecurringRules';
import useRuleDescription from '@/enterprise/features/recurringRules/utils/useRuleDescription';

type RuleChipConfig = {
  [key in keyof Pick<RecurringRule, 'dpe' | 'dueDate' | 'amount' | 'excludedVoucherIds'>]: {
    label: string;
  };
};

interface RuleChipsProps {
  recurringRule: RecurringRule;
  bold?: boolean;
  inline?: boolean;
  inverse?: boolean;
  variant?: 'light' | 'dark';
  withLabel?: boolean;
}

const RuleChips = ({ bold, inline, inverse, recurringRule, variant, withLabel }: RuleChipsProps) => {
  const { daysPaidEarly, dueDate, invoiceAmount, excludedInvoiceIds, labelText, rulesToRender } = useRuleDescription({
    currency: recurringRule.currency,
    rule: recurringRule,
  });

  const ruleChipConfig: RuleChipConfig = {
    dpe: { label: daysPaidEarly.short ?? '' },
    dueDate: { label: dueDate.short ?? '' },
    amount: { label: invoiceAmount.short ?? '' },
    excludedVoucherIds: { label: excludedInvoiceIds.short ?? '' },
  };

  return rulesToRender.length > 0 ? (
    <div className={cn('space-y-2', { 'flex flex-wrap items-center gap-2 space-y-0': inline })}>
      {withLabel && (
        <div className={cn('text-sm text-text-secondary', { 'font-medium': bold, 'text-white': inverse })}>
          {labelText}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {rulesToRender.map((key) => {
          const config = ruleChipConfig[key as keyof typeof ruleChipConfig]!;

          const chipElement = (
            <Chip
              key={key}
              label={config.label}
              type="info"
              variant="outlined"
              {...(variant === 'dark' && { className: 'border-blueGray-500 bg-blueGray-50 text-blueGray-900' })}
            />
          );

          if (key === 'excludedVoucherIds' && config.label) {
            return (
              <Tooltip key={key}>
                <Tooltip.Trigger>{chipElement}</Tooltip.Trigger>
                <Tooltip.Content
                  arrow
                  side="bottom"
                  variant="dark"
                  className="max-w-60 whitespace-pre-wrap text-center"
                >
                  {/* If rulesToRender has excludedVoucherIds key then we know invoice IDs are defined */}
                  <div className="text-sm">{recurringRule.excludedVoucherIds!.join(', ')}</div>
                </Tooltip.Content>
              </Tooltip>
            );
          }

          return chipElement;
        })}
      </div>
    </div>
  ) : null;
};

export default RuleChips;
